import cityUpgrades from '@/config/cityUpgrades';
import { useCitiesStore } from '@/hooks/useCitiesStore';
import { useShittyMapCamera } from '@/hooks/useShittyMapCamera';
import { useShittyMapStore } from '@/hooks/useShittyMapStore';
import { calculateCityPositions } from '@/utils/shittyMapLayout';
import { useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export default function ShittyMapFilterBar() {
  const { allCities } = useCitiesStore();
  const { filters, setFilter, clearFilters, setHighlightedCities, setSelectedCity, setDetailPanelOpen } = useShittyMapStore();
  const { focusOnCity, showAllCities, flyToMultiple } = useShittyMapCamera();

  const { positions } = useMemo(() => calculateCityPositions(allCities), [allCities]);
  const positionMap = useMemo(() => new Map(positions.map((p) => [p.cityIndex, p])), [positions]);

  const filteredCities = useMemo(() => {
    return allCities.filter((city) => {
      if (filters.searchQuery) {
        const name = (city.details?.name || city.params?.name || '').toLowerCase();
        const indexStr = city.index.toString();
        const query = filters.searchQuery.toLowerCase();
        if (!name.includes(query) && !indexStr.includes(query)) {
          return false;
        }
      }

      if (filters.upgrade) {
        const cityUpgrade = city.metadata?.properties?.Upgrade || 'None';
        if (cityUpgrade !== filters.upgrade) {
          return false;
        }
      }

      if (filters.blockCount !== '') {
        const count = city.metadata?.properties?.['Block Count'] || 0;
        if (count !== parseInt(filters.blockCount)) {
          return false;
        }
      }

      return true;
    });
  }, [allCities, filters]);

  const debouncedApplyFilter = useDebouncedCallback(() => {
    const indices = filteredCities.map((c) => c.index);
    setHighlightedCities(indices);

    if (filteredCities.length === 1) {
      const city = filteredCities[0];
      const pos = positionMap.get(city.index);
      if (pos) {
        focusOnCity(pos.x, pos.y);
        setSelectedCity(city);
        setDetailPanelOpen(true);
      }
    } else if (filteredCities.length > 1 && filteredCities.length < allCities.length) {
      const filteredPositions = filteredCities.map((c) => positionMap.get(c.index)).filter(Boolean);
      flyToMultiple(filteredPositions);
    } else if (filteredCities.length === allCities.length) {
      setHighlightedCities([]);
    }
  }, 300);

  useEffect(() => {
    debouncedApplyFilter();
  }, [filters.searchQuery, filters.upgrade, filters.blockCount]);

  const handleClear = () => {
    clearFilters();
    showAllCities();
  };

  const hasActiveFilters = filters.searchQuery || filters.upgrade || filters.blockCount !== '';

  return (
    <div className='absolute top-4 left-4 right-4 z-10 pointer-events-none'>
      <div className='inline-flex flex-wrap gap-2.5 bg-gray-900/95 backdrop-blur-xl p-3 rounded-xl border border-gray-800 shadow-2xl pointer-events-auto'>
        <div className='flex-1 min-w-[180px]'>
          <input
            type='text'
            value={filters.searchQuery}
            onChange={(e) => setFilter('searchQuery', e.target.value)}
            placeholder='Search by name or ID...'
            className='w-full bg-gray-800/80 border border-gray-700/50 rounded-lg px-3.5 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all'
          />
        </div>

        <select
          value={filters.upgrade}
          onChange={(e) => setFilter('upgrade', e.target.value)}
          className='bg-gray-800/80 border border-gray-700/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 min-w-[140px] cursor-pointer transition-all'
        >
          <option value=''>All Upgrades</option>
          <option value='None'>No Upgrade</option>
          {cityUpgrades.map((u) => (
            <option key={u.name} value={u.name}>
              {u.name}
            </option>
          ))}
        </select>

        <select
          value={filters.blockCount}
          onChange={(e) => setFilter('blockCount', e.target.value)}
          className='bg-gray-800/80 border border-gray-700/50 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 min-w-[120px] cursor-pointer transition-all'
        >
          <option value=''>All Blocks</option>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <option key={n} value={n}>
              {n} Blocks
            </option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            onClick={handleClear}
            className='bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700/50 rounded-lg px-3.5 py-2 text-gray-300 hover:text-white text-sm transition-all'
          >
            Clear
          </button>
        )}

        <div className='flex items-center text-sm ml-auto px-2'>
          <span className='font-bold text-primary-500'>{filteredCities.length}</span>
          <span className='text-gray-500 mx-1'>of</span>
          <span className='text-gray-400'>{allCities.length}</span>
          <span className='text-gray-500 ml-1'>cities</span>
        </div>
      </div>
    </div>
  );
}
